import { Box, Button, CircularProgress, Stack } from "@mui/material";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { AttributeValue } from "@sinch/components/application/Attribute/AttributeValue";
import { CompanyHeader } from "@sinch/components/application/Company/CompanyHeader/CompanyHeader";
import { MdsIcon } from "@sinch/components";
import { MdsMailXs, MdsOpenInNewXs } from "@sinch/icons";
import { CompanyTooltipQuery } from "./queries.types";

type TooltipCompany = NonNullable<CompanyTooltipQuery["company"]>;

interface CompanyTooltipContentProps {
  company: TooltipCompany | null | undefined;
}

/**
 * Body of the company hover card: the profile header, the company's important attributes and
 * the quick actions. Shows a spinner until the lazily fetched company arrives.
 */
export const CompanyTooltipContent: FC<CompanyTooltipContentProps> = ({ company }) => {
  const { t } = useTranslation();

  if (!company) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", minHeight: 80, minWidth: 240, p: 5 }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <>
      <CompanyHeader avatarSize="xl" company={company} />

      <Stack direction="column">
        {(company.attributes ?? []).map(({ id, value, attribute, files, expirationDate }) => (
          <AttributeValue
            key={id}
            expirationDate={expirationDate}
            files={files}
            label={attribute.name}
            options={attribute.options}
            type={attribute.dataType}
            value={value}
          />
        ))}
      </Stack>
      <Stack direction="row" gap={1} sx={{ pt: 1 }}>
        {company.emailInvoice && (
          <Button
            href={`mailto:${company.emailInvoice}`}
            size="small"
            startIcon={<MdsIcon fontSize="xs" icon={MdsMailXs} />}
            variant="outlined"
          >
            {t("Messages.channelEmail")}
          </Button>
        )}
        <Button
          component="a"
          endIcon={<MdsIcon fontSize="xs" icon={MdsOpenInNewXs} />}
          href={`/admin/companies/view/${company.id}`}
          rel="noopener noreferrer"
          size="small"
          sx={{ ml: "auto" }}
          target="_blank"
          variant="outlined"
        >
          {t("Action.view")}
        </Button>
      </Stack>
    </>
  );
};
